import React, { useState } from 'react';
import { Box, Typography, Link as MuiLink, CardMedia, Button, Snackbar, Alert, IconButton  } from '@mui/material';
import { useParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import HeroBanner from '../components/HeroBanner';
import Footer from '../components/Footer';
import Breadcrumb from '../components/Breadcrumb';
import ReactMarkdown from 'react-markdown';
import { modelItems } from '../data/SiteData';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';

const ModelPage = () => {
    const { modelId } = useParams();
    const navigate = useNavigate();
    const model = modelItems.find((item) => item.modelId === modelId);

    const [snackbarOpen, setSnackbarOpen] = useState(false);

  const handleDownloadClick = () => {
    setSnackbarOpen(true);
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbarOpen(false);
  };

    if (!model) return <Typography>Model not found.</Typography>;

    

    const breadcrumbItems = [
        { title: 'Home', path: '/' },
        { title: 'Models', path: '/models' },
        {
            title:
                model.title.length > 30
                    ? model.title.substring(0, 30) + '...'
                    : model.title,
            path: model.link,
        },
    ];

    return (
        <>
            <Navbar />
            <HeroBanner
                heroTitle="Models"
                heroDescription="Find models and research data.."
                imageUrl="/images/digiTwins.png"
                height="165px"
            />
            <Breadcrumb items={breadcrumbItems} />

            <Box
                sx={{
                    display: 'flex',
                    padding: '32px',
                    maxWidth: '1300px',
                    margin: '0 auto',
                    gap: '32px',
                }}
            >
                {/* Left-hand metadata column */}
                <Box
                    sx={{
                        width: '240px',
                        paddingRight: '32px',
                        flexShrink: 0,
                    }}
                >
                    {/* IMAGE */}
                    {model.image && (
                        <CardMedia
                            component="img"
                            image={model.image}
                            alt={model.title}
                            sx={{ marginBottom: '16px', width: '100%' }}
                        />
                    )}

                    {/* IDENTIFIER */}
                    <Typography
                        sx={{
                            fontFamily: 'PT Sans',
                            fontWeight: 'bold',
                            fontSize: '16px',
                            color: '#142434',
                            textTransform: 'uppercase',
                            marginTop: '16px',
                        }}
                    >
                        Identifier
                    </Typography>
                    <Typography sx={{ fontFamily: 'PT Sans', marginBottom: '8px' }}>
                        {model.idVal}
                    </Typography>

                    {/* SOURCE */}
                    <Typography
                        sx={{
                            fontFamily: 'PT Sans',
                            fontWeight: 'bold',
                            fontSize: '16px',
                            color: '#142434',
                            textTransform: 'uppercase',
                            marginTop: '16px',
                        }}
                    >
                        Source
                    </Typography>
                    <Typography sx={{ fontFamily: 'PT Sans', marginBottom: '8px' }}>
                        {model.source}
                    </Typography>

                    {/* RESEARCH FOCUS */}
                    <Typography
                        sx={{
                            fontFamily: 'PT Sans',
                            fontWeight: 'bold',
                            fontSize: '16px',
                            color: '#142434',
                            textTransform: 'uppercase',
                            marginTop: '16px',
                        }}
                    >
                        Research Focus
                    </Typography>
                    {model.foci?.map((f, i) => (
                        <Typography key={i} sx={{ fontFamily: 'PT Sans', marginBottom: '4px' }}>
                            {f.focus}
                        </Typography>
                    ))}

                    {/* CONDITION */}
                    <Typography
                        sx={{
                            fontFamily: 'PT Sans',
                            fontWeight: 'bold',
                            fontSize: '16px',
                            color: '#142434',
                            textTransform: 'uppercase',
                            marginTop: '16px',
                        }}
                    >
                        Condition
                    </Typography>
                    {model.conditions?.map((c, i) => (
                        <Typography key={i} sx={{ fontFamily: 'PT Sans', marginBottom: '4px' }}>
                            {c.condition}
                        </Typography>
                    ))}

                    {/* ORGANIZATION */}
                    <Typography
                        sx={{
                            fontFamily: 'PT Sans',
                            fontWeight: 'bold',
                            fontSize: '16px',
                            color: '#142434',
                            textTransform: 'uppercase',
                            marginTop: '16px',
                        }}
                    >
                        Organization
                    </Typography>
                    <Typography sx={{ fontFamily: 'PT Sans', marginBottom: '8px' }}>
                        {model.affiliation}
                    </Typography>

                    {/* PERSONNEL */}
                    <Typography
                        sx={{
                            fontFamily: 'PT Sans',
                            fontWeight: 'bold',
                            fontSize: '16px',
                            color: '#142434',
                            textTransform: 'uppercase',
                            marginTop: '16px',
                        }}
                    >
                        Personnel
                    </Typography>
                    {model.authors?.map((author, i) => (
                        <Typography key={i} sx={{ fontFamily: 'PT Sans', marginBottom: '4px' }}>
                            {author.orcid ? (
                                <MuiLink
                                    href={`https://orcid.org/${author.orcid}`}
                                    target="_blank"
                                    rel="noopener"
                                    sx={{
                                        fontFamily: 'PT Sans',
                                        color: '#489596',
                                        textDecoration: 'none',
                                        '&:hover': { textDecoration: 'underline' },
                                    }}
                                >
                                    {author.name}
                                </MuiLink>
                            ) : (
                                author.name
                            )}
                        </Typography>
                    ))}

                </Box>



                {/* RIGHT COLUMN */}
<Box sx={{ flex: 1, paddingLeft: '24px' }}>
  {/* TITLE */}
  <Typography
    sx={{ fontFamily: 'Poppins', fontSize: '24px', fontWeight: 400, color: '#142434', marginBottom: '8px' }}
  >
    {model.title}
  </Typography>

  {/* DATE */}
  <Typography
    sx={{ fontFamily: 'PT Sans', fontSize: '16px', fontWeight: 400, color: 'rgba(20, 36, 52, 0.7)', marginBottom: '4px' }}
  >
    {model.date}
  </Typography>

  {/* TYPE */}
  <Typography
    sx={{ fontFamily: 'PT Sans', fontSize: '16px', fontWeight: 400, color: 'rgba(20, 36, 52, 0.7)', textTransform: 'uppercase', marginBottom: '8px' }}
  >
    {model.type}
  </Typography>

  {model.type === 'internal' ? (
    <>
      {/* PUBLICATION */}
      <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
        <Typography sx={{ fontFamily: 'PT Sans', fontSize: '16px', fontWeight: 700, color: '#142434', marginRight: '8px' }}>
          Publication:
        </Typography>
        <MuiLink
          href={model.pubLink}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            fontFamily: 'PT Sans',
            fontSize: '16px',
            fontWeight: 400,
            color: '#489596',
            textDecoration: 'none',
            '&:hover': { textDecoration: 'underline' },
          }}
        >
          {model.pubLink}
        </MuiLink>
      </Box>

      {/* DESCRIPTION */}
      <Typography sx={{ fontFamily: 'PT Sans', fontSize: '16px', fontWeight: 400, color: '#142434', marginBottom: '12px' }}>
        {model.description}
      </Typography>

      {/* DIVIDER */}
      <Box sx={{ height: '0.5px', backgroundColor: '#489596', marginY: '12px' }} />

      {/* VERSION */}
      <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
        <Typography sx={{ fontFamily: 'PT Sans', fontSize: '16px', fontWeight: 700, color: '#142434', marginRight: '8px' }}>
          VERSION:
        </Typography>
        <Typography sx={{ fontFamily: 'PT Sans', fontSize: '16px', fontWeight: 400, color: '#142434' }}>
          {model.version}
        </Typography>
      </Box>

      {/* FILE SIZE */}
      <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
        <Typography sx={{ fontFamily: 'PT Sans', fontSize: '16px', fontWeight: 700, color: '#142434', marginRight: '8px' }}>
          FILE SIZE:
        </Typography>
        <Typography sx={{ fontFamily: 'PT Sans', fontSize: '16px', fontWeight: 400, color: '#142434' }}>
          {model.fileSize}
        </Typography>
      </Box>

      {/* BUTTONS */}
      <Box sx={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
        <Button
          variant="contained"
          sx={{
            width: '200px',
            height: '40px',
            fontFamily: 'Poppins, sans-serif',
            fontSize: '18px',
            fontWeight: 600,
            color: '#FFFFFF',
            boxShadow: 'none',
            backgroundColor: '#489596',
            border: 'none',
            cursor: 'pointer',
            textTransform: 'none',
          }}
          onClick={handleDownloadClick}
        >
          Download Model
        </Button>
        <Button
          variant="contained"
          sx={{
            width: '200px',
            height: '40px',
            fontFamily: 'Poppins, sans-serif',
            fontSize: '18px',
            fontWeight: 600,
            color: '#FFFFFF',
            boxShadow: 'none',
            backgroundColor: '#489596',
            border: 'none',
            cursor: 'pointer',
            textTransform: 'none',
          }}
          onClick={() => navigate(model.simLink)}
        >
          Run Simulation
        </Button>
      </Box>

      {/* DIVIDER */}
      <Box sx={{ height: '0.5px', backgroundColor: '#489596', marginY: '16px' }} />
    </>
  ) : (
    <>
      {/* EXTERNAL LINK */}
      <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
        <Typography sx={{ fontFamily: 'PT Sans', fontSize: '16px', fontWeight: 700, color: '#142434', marginRight: '8px' }}>
          View Model:
        </Typography>
        <MuiLink
          href={model.extLink}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            fontFamily: 'PT Sans',
            fontSize: '16px',
            fontWeight: 400,
            color: '#489596',
            textDecoration: 'none',
            '&:hover': { textDecoration: 'underline' },
          }}
        >
          {model.extLink}
        </MuiLink>
      </Box>

      {/* DESCRIPTION */}
      <Typography sx={{ fontFamily: 'PT Sans', fontSize: '16px', fontWeight: 400, color: '#142434', marginBottom: '12px' }}>
        {model.description}
      </Typography>

      {/* DIVIDER */}
      <Box sx={{ height: '0.5px', backgroundColor: '#489596', marginY: '16px' }} />
    </>
  )}

  {/* MAIN CONTENT */}
  <Box sx={{ fontFamily: 'PT Sans', fontSize: '16px', color: '#142434' }}>
    <ReactMarkdown>{model.mainContent}</ReactMarkdown>
  </Box>
</Box>


            </Box>

            <Footer />


      {/* SNACKBAR ALERT */}
      <Snackbar
        open={snackbarOpen}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ top: '16px', width: 'auto', padding: '0 16px', zIndex: 1300 }}
        onClose={handleCloseSnackbar}
      >
        <Alert
          severity="info"
          onClose={handleCloseSnackbar}
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={handleCloseSnackbar}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
          sx={{
            width: '100%',
            border: "1px solid",
            borderColor: "info.main",
            p: 2,
          }}
        >
          Downloading files is not available in the demonstration prototype
        </Alert>
      </Snackbar>
        </>
    );
};

export default ModelPage;
